import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { TempleBellIcon } from './SacredArtifacts';
import './signature-effects.css';

/** Synthesize a short brass temple-bell chime (no external asset). */
function playTempleBellTone(slot: number) {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const fundamental = 620 + slot * 90;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.28, now + 0.02);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 1.35);
    master.connect(ctx.destination);

    const partials = [1, 2.01, 2.98, 4.12];
    partials.forEach((partial, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = i === 0 ? 'sine' : 'triangle';
      osc.frequency.setValueAtTime(fundamental * partial, now);
      g.gain.setValueAtTime(0.22 / (i + 1), now);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 1.1 - i * 0.12);
      osc.connect(g);
      g.connect(master);
      osc.start(now);
      osc.stop(now + 1.4);
    });

    window.setTimeout(() => {
      void ctx.close().catch(() => undefined);
    }, 1600);
  } catch {
    /* autoplay / AudioContext blocked — visual swing still runs */
  }
}

/** ─── VEDIC ONLY: tap brass bells → swing + golden ripple + chime ─── */
export function TempleBellRack({ className = '' }: { className?: string }) {
  const [rings, setRings] = useState<{ id: number; slot: number }[]>([]);
  const [swinging, setSwinging] = useState<Record<number, number>>({});

  const ring = (slot: number) => {
    const id = Date.now() + slot;
    setSwinging((s) => ({ ...s, [slot]: (s[slot] ?? 0) + 1 }));
    setRings((r) => [...r.slice(-4), { id, slot }]);
    playTempleBellTone(slot);
    window.setTimeout(() => {
      setRings((r) => r.filter((x) => x.id !== id));
    }, 1200);
  };

  return (
    <div className={`sig-bells ${className}`} data-signature="vedic-bells">
      <p className="sig-bells-hint">Tap a bell for blessings</p>
      <div className="sig-bells-row">
        {[0, 1, 2].map((slot) => (
          <button
            key={slot}
            type="button"
            className="sig-bell"
            aria-label={`Ring temple bell ${slot + 1}`}
            onClick={(e) => {
              e.stopPropagation();
              ring(slot);
            }}
          >
            <span
              key={swinging[slot] ?? 0}
              className={`sig-bell-body ${swinging[slot] ? 'is-swinging' : ''}`}
            >
              <TempleBellIcon size={54} className="sig-bell-img" />
            </span>
            {rings
              .filter((r) => r.slot === slot)
              .map((r) => (
                <span key={r.id} className="sig-bell-ripple" aria-hidden />
              ))}
          </button>
        ))}
      </div>
    </div>
  );
}

/** ─── VEDIC ONLY: slow spinning rangoli / mandala behind names ─── */
export function SpinningRangoli({ className = '' }: { className?: string }) {
  return (
    <div className={`sig-rangoli ${className}`} data-signature="vedic-rangoli" aria-hidden>
      <svg viewBox="0 0 200 200" className="sig-rangoli-svg">
        <defs>
          <radialGradient id="sigRangoliGold" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#f0a820" stopOpacity="0.55" />
            <stop offset="55%" stopColor="#e0c068" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#5c0f1c" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="100" cy="100" r="88" fill="url(#sigRangoliGold)" />
        {Array.from({ length: 12 }, (_, i) => {
          const a = (i * 30 * Math.PI) / 180;
          const x2 = 100 + Math.cos(a) * 78;
          const y2 = 100 + Math.sin(a) * 78;
          return (
            <g key={i}>
              <line x1="100" y1="100" x2={x2} y2={y2} stroke="#e0c068" strokeWidth="1.2" opacity="0.55" />
              <circle cx={x2} cy={y2} r="5" fill="#f0a820" opacity="0.7" />
            </g>
          );
        })}
        <circle cx="100" cy="100" r="22" fill="none" stroke="#e0c068" strokeWidth="1.5" opacity="0.6" />
        <circle cx="100" cy="100" r="8" fill="#f0a820" opacity="0.85" />
      </svg>
    </div>
  );
}

/** ─── EMERALD ONLY: velvet curtains part once after seal opens ─── */
export function VelvetCurtainPart({
  active,
  children,
}: {
  active: boolean;
  children: React.ReactNode;
}) {
  const [parted, setParted] = useState(false);

  useEffect(() => {
    if (!active) return;
    // Hold closed briefly so guests see the velvet before it parts
    const t = window.setTimeout(() => setParted(true), 900);
    return () => window.clearTimeout(t);
  }, [active]);

  return (
    <div className="sig-curtain-root" data-signature="emerald-curtain">
      {children}
      <div className={`sig-curtain sig-curtain--left ${parted ? 'is-parted' : ''}`} aria-hidden />
      <div className={`sig-curtain sig-curtain--right ${parted ? 'is-parted' : ''}`} aria-hidden />
      {!parted ? <p className="sig-curtain-tag">Opening the emerald room…</p> : null}
    </div>
  );
}

/** ─── EMERALD ONLY: green/gold fireflies (not petals, not sand) ─── */
export function EmeraldFireflies({ count = 18, className = '' }: { count?: number; className?: string }) {
  const flies = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: `${(i * 47 + 3) % 100}%`,
        top: `${(i * 31 + 9) % 90}%`,
        delay: `${(i % 9) * 0.4}s`,
        duration: `${5 + (i % 6)}s`,
        size: 3 + (i % 4),
        hue: i % 2 === 0 ? '#7dffa0' : '#e8c96a',
      })),
    [count],
  );
  return (
    <div className={`sig-fireflies ${className}`} data-signature="emerald-fireflies" aria-hidden>
      {flies.map((f) => (
        <span
          key={f.id}
          className="sig-firefly"
          style={{
            left: f.left,
            top: f.top,
            width: f.size,
            height: f.size,
            background: f.hue,
            boxShadow: `0 0 10px 3px ${f.hue}`,
            animationDelay: f.delay,
            animationDuration: f.duration,
          }}
        />
      ))}
    </div>
  );
}

/** ─── SCRATCH ONLY: paper envelope unfolds after foil reveal ─── */
export function EnvelopeUnfold({
  active,
  children,
}: {
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="sig-envelope" data-signature="scratch-envelope">
      <motion.div
        className={`sig-envelope-flap ${active ? 'is-open' : ''}`}
        aria-hidden
        initial={false}
        animate={active ? { rotateX: -168, opacity: 0.35 } : { rotateX: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
      />
      <motion.div
        className="sig-envelope-body"
        initial={false}
        animate={active ? { y: 0, scale: 1 } : { y: 8, scale: 0.98 }}
        transition={{ duration: 0.55, delay: active ? 0.15 : 0 }}
      >
        {children}
      </motion.div>
    </div>
  );
}
